export function isGroupedSeries(input) {
  if (!Array.isArray(input) || input.length === 0) return false;
  const s = input[0];
  return (
    s &&
    typeof s === "object" &&
    "seriesName" in s &&
    Array.isArray(s.seriesData)
  );
}

function ensureNumber(v) {
  return v != null && isFinite(v) ? Number(v) : 0;
}

function aggregate(values, mode) {
  const nums = values.map(ensureNumber);
  if (mode === "avg") {
    const sum = nums.reduce((a, b) => a + b, 0);
    const n = nums.length || 1;
    return sum / n;
  }
  const sum = nums.reduce((a, b) => a + b, 0);
  return sum;
}

function collectInnerNames(groups) {
  const set = new Set();
  for (const g of groups) {
    for (const it of g.seriesData || []) {
      if (it && typeof it.name === "string") set.add(it.name);
    }
  }
  return Array.from(set);
}

function defaultNameFormat({ outer, inner }) {
  const o = outer || "";
  const i = inner || "";
  return i && o ? `${i} • ${o}` : i || o || "Series";
}

export function normalizeForChart({
  chartType,
  inputSeries,
  categories,
  options,
}) {
  const groupedOpts = (options && options.grouped) || {};
  const pieReduce = (options && options.pieReduce) || {};
  const nameFormat = groupedOpts.nameFormat || defaultNameFormat;
  const isGrouped = isGroupedSeries(inputSeries);
  if (!isGrouped) {
    if (chartType === "pie") {
      const mode =
        (options && options.pie && options.pie.mode) ||
        (options && options.pieMode) ||
        "auto";
      const src = Array.isArray(inputSeries) ? inputSeries : [];
      const cats = Array.isArray(categories) ? categories : [];
      if (mode === "categorySlices") {
        const seriesIndex =
          typeof options?.pie?.seriesIndex === "number"
            ? Math.max(0, options.pie.seriesIndex)
            : 0;
        const s = src[seriesIndex] || src[0] || { name: "data", data: [] };
        const values = Array.isArray(s.data)
          ? s.data.map(ensureNumber)
          : [ensureNumber(s.data)];
        const labels =
          cats.length === values.length
            ? cats
            : values.map((_, i) => `Slice ${i + 1}`);
        return { series: [{ name: "data", data: values }], categories: labels };
      }
      if (src.length > 1 && mode !== "categorySlices") {
        const labels = src.map((s, i) => s?.name || `Series ${i + 1}`);
        const values = src.map((s) => {
          const d = s && s.data;
          return Array.isArray(d) ? aggregate(d, "sum") : ensureNumber(d);
        });
        return { series: [{ name: "data", data: values }], categories: labels };
      }
      const first = src[0] || { name: "data", data: [] };
      const values = Array.isArray(first.data)
        ? first.data.map(ensureNumber)
        : [ensureNumber(first.data)];
      const labels =
        cats.length === values.length
          ? cats
          : values.map((_, i) => `Slice ${i + 1}`);
      return { series: [{ name: "data", data: values }], categories: labels };
    }
    return {
      series: Array.isArray(inputSeries) ? inputSeries : [],
      categories: Array.isArray(categories) ? categories : [],
    };
  }
  const groups = inputSeries;
  const innerNames = collectInnerNames(groups);
  const orientation = groupedOpts.orientation || "byCategory";
  const flatten = groupedOpts.flatten || "outerInner";
  const agg = groupedOpts.aggregateAcrossGroups || "none";
  if (chartType === "pie") {
    if (typeof pieReduce.categoryIndex === "number") {
      const catIdx = Math.max(0, pieReduce.categoryIndex);
      const innerIdx = Math.min(
        Math.max(0, pieReduce.innerIndex || 0),
        Math.max(0, innerNames.length - 1)
      );
      const labels = groups.map((g) => g.seriesName || "");
      const vals = groups.map((g) => {
        const it =
          (g.seriesData || []).find((x) => x.name === innerNames[innerIdx]) ||
          g.seriesData?.[innerIdx];
        const v = it && Array.isArray(it.data) ? it.data[catIdx] : undefined;
        return ensureNumber(v);
      });
      return { series: [{ name: "data", data: vals }], categories: labels };
    }
    if (typeof pieReduce.innerIndex === "number") {
      const innerIdx = Math.min(
        Math.max(0, pieReduce.innerIndex),
        Math.max(0, innerNames.length - 1)
      );
      const labels = groups.map((g) => g.seriesName || "");
      const vals = groups.map((g) => {
        const it =
          (g.seriesData || []).find((x) => x.name === innerNames[innerIdx]) ||
          g.seriesData?.[innerIdx];
        const arr = it && Array.isArray(it.data) ? it.data : [];
        return aggregate(arr, pieReduce.aggregate || "sum");
      });
      return { series: [{ name: "data", data: vals }], categories: labels };
    }
    const labels = innerNames;
    const vals = innerNames.map((nm) => {
      const collected = [];
      for (const g of groups) {
        const it = (g.seriesData || []).find((x) => x.name === nm);
        if (it && Array.isArray(it.data)) {
          for (const v of it.data) collected.push(ensureNumber(v));
        }
      }
      return aggregate(collected, pieReduce.aggregate || "sum");
    });
    return { series: [{ name: "data", data: vals }], categories: labels };
  }
  if (orientation === "byGroup") {
    const labels = groups.map((g) => g.seriesName || "");
    const catIdx =
      typeof groupedOpts.categoryIndex === "number"
        ? Math.max(0, groupedOpts.categoryIndex)
        : 0;
    const out = innerNames.map((nm, j) => {
      const data = groups.map((g) => {
        const it =
          (g.seriesData || []).find((x) => x.name === nm) || g.seriesData?.[j];
        const v = it && Array.isArray(it.data) ? it.data[catIdx] : undefined;
        return ensureNumber(v);
      });
      return { name: nm, data };
    });
    return { series: out, categories: labels };
  }
  if (flatten === "groupStacked") {
    const labels = Array.isArray(categories) ? categories : [];
    const out = [];
    for (let gIdx = 0; gIdx < groups.length; gIdx += 1) {
      const g = groups[gIdx];
      const outerName = g?.seriesName || "";
      const items = Array.isArray(g?.seriesData) ? g.seriesData : [];
      for (let j = 0; j < items.length; j += 1) {
        const it = items[j];
        const innerName =
          it && typeof it.name === "string"
            ? it.name
            : innerNames[j] || `Series ${j + 1}`;
        const arr = Array.isArray(it?.data) ? it.data.map(ensureNumber) : [];
        out.push({
          name: innerName,
          data: arr,
          __group: gIdx,
          __groupName: outerName,
          __groupSeriesId: g?.seriesId ?? null,
        });
      }
    }
    return { series: out, categories: labels };
  }
  if (flatten === "inner") {
    const nCats = Math.max(
      0,
      Array.isArray(categories) ? categories.length : 0
    );
    const labels = Array.isArray(categories) ? categories : [];
    const out = innerNames.map((nm, j) => {
      const data = [];
      for (let i = 0; i < nCats; i++) {
        const values = groups.map((g) => {
          const it =
            (g.seriesData || []).find((x) => x.name === nm) ||
            g.seriesData?.[j];
          const v = it && Array.isArray(it.data) ? it.data[i] : undefined;
          return ensureNumber(v);
        });
        data.push(agg === "none" ? values[0] : aggregate(values, agg));
      }
      return { name: nm, data };
    });
    return { series: out, categories: labels };
  }
  const labels = Array.isArray(categories) ? categories : [];
  const out = [];
  for (const g of groups) {
    const outerName = g.seriesName || "";
    const items = Array.isArray(g.seriesData) ? g.seriesData : [];
    for (let j = 0; j < items.length; j++) {
      const it = items[j];
      const innerName =
        it && typeof it.name === "string"
          ? it.name
          : innerNames[j] || `Series ${j + 1}`;
      const nm = nameFormat({ outer: outerName, inner: innerName });
      const arr = Array.isArray(it?.data) ? it.data.map(ensureNumber) : [];
      out.push({ name: nm, data: arr });
    }
  }
  return { series: out, categories: labels };
}
